import React, { useState } from "react";
import ContentRecommendationTile from "../ContentRecommendationTile";
import { RecommendationVariant, TileRecommendation } from "../types";
import styles from "./index.style.module.css";

interface ContentRecommendationBlockProps {
  variant: RecommendationVariant;
  icon?: React.ReactNode;
  title: string;
  count?: number;
  recommendations: TileRecommendation[];
  onToggle?: (id: string, selected: boolean) => void;
  className?: string;
  defaultExpanded?: boolean;
}

const ContentRecommendationBlock: React.FC<ContentRecommendationBlockProps> = ({
  variant: _variant,
  icon,
  title,
  count,
  recommendations,
  onToggle,
  className = "",
  defaultExpanded = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Filter out recommendations with no items
  const filteredRecommendations = recommendations.filter(
    (rec) => rec.items.length > 0,
  );

  const totalCount =
    count ??
    filteredRecommendations.reduce((sum, rec) => sum + rec.items.length, 0);

  const handleToggle = (id: string, selected: boolean) => {
    if (onToggle) {
      onToggle(id, selected);
    }
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Don't render the block if there are no recommendations with items
  if (filteredRecommendations.length === 0) {
    return null;
  }

  const groupedRecommendations = filteredRecommendations.reduce(
    (groups, rec) => {
      const label = rec.label || "Other";
      if (!groups[label]) {
        groups[label] = [];
      }
      groups[label].push(rec);
      return groups;
    },
    {} as Record<string, TileRecommendation[]>,
  );

  return (
    <div className={`${styles.block} ${className}`}>
      <button
        className={styles.header}
        onClick={toggleExpand}
        aria-expanded={isExpanded}
        type="button"
      >
        <div className={styles.headerLeft}>
          {icon && <div className={styles.icon}>{icon}</div>}
          <span className={styles.title}>{title}</span>
          <span className={styles.count}>({totalCount})</span>
        </div>
        <div className={styles.chevron}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            style={{
              transform: isExpanded ? "rotate(0deg)" : "rotate(180deg)",
              transition: "transform 0.2s",
            }}
          >
            <path
              d="M7.41 15.41L12 10.83L16.59 15.41L18 14L12 8L6 14L7.41 15.41Z"
              fill="#18181b"
            />
          </svg>
        </div>
      </button>
      {isExpanded && (
        <div className={styles.content}>
          {Object.entries(groupedRecommendations).map(([label, recs]) => (
            <div
              key={label}
              className={`${styles.container} ${recs[0]?.variant ? styles[recs[0].variant] : ""}`}
            >
              {recs.map((rec, index) => (
                <ContentRecommendationTile
                  key={rec.id}
                  variant={rec.variant}
                  label={index === 0 ? rec.label : undefined}
                  recommendations={rec.items}
                  onToggle={handleToggle}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContentRecommendationBlock;
